/**
 * My Cards data contracts (PRD 03 §6.3 / §8.2) shared by the web page
 * (client/src/site/components/my-cards) and the mobile My Cards tab
 * (mobile/src/components/my-cards). Dependency-free like reminders-data /
 * composer-data — the Eden fetchers live in each client, only the row shapes,
 * query keys, and pure formatters live here so the two lanes can't drift.
 *
 * The `/user/my-cards/*` list endpoints return `t.Any()` server-side (Kysely
 * rows), so the row types below hand-type the slice the UI actually reads.
 */

import { MONTH_NAMES, ordinal } from "./format";
import type { GiftStatus } from "./gift-data";

/** Shared gift-status vocabulary — one source so the two lanes can't drift (see gift-data.ts). */
export type { GiftStatus };

/** Legacy page size (old MUI TablePagination showed "1-10 of N"). */
export const MY_CARDS_PAGE_SIZE = 10;

/**
 * The card's still-art geometry + dominant color, stamped on every list row
 * server-side (`withCardArtMeta`). The thumb reserves an `art_width / art_height`
 * box tinted `art_color` BEFORE the per-row card fetch lands — no layout shift.
 * Nullable: a card with no ready art, or a server older than this field.
 */
export interface CardArtMeta {
  art_width?: number | null;
  art_height?: number | null;
  art_color?: string | null;
  /**
   * The card's art, ready to render (server `loadCardArtMeta`). Prefer it over fetching
   * the card detail per row — that costs 3 queries and 1200px urls for a thumbnail.
   * Optional: an older server won't send it, so the per-row fetch stays as the fallback.
   */
  art_url?: string | null;
  /**
   * SEND rows only (server `withSendArt`): the photo card's flattened snapshot — the
   * finished card, which is what the row shows instead of the template art. When set,
   * the art_* fields describe IT. Null → render the catalog card's art as usual (also
   * for a photo-card draft not yet customized: the template is the honest picture).
   */
  snapshot_url?: string | null;
}

/** `width / height` of the row's art, or null when unknown. */
export function artAspect(row: CardArtMeta | null | undefined): number | null {
  return row?.art_width && row.art_height ? row.art_width / row.art_height : null;
}

export interface InboxRow extends CardArtMeta {
  card_send_recipient_id: string;
  card_send_id: string;
  recipient_status: string;
  sent_at: string | null;
  opened_at: string | null;
  saved_at: string | null;
  is_hidden: boolean;
  thanked_at: string | null;
  from_email: string | null;
  from_name: string | null;
  /** Nullable: lazily set at schedule; migrated sends may lack it. */
  read_slug: string | null;
  signers: SignerEntry[];
  /** BIGINT — serialized as a string by the API (no int8 parser). */
  card_no: string;
  slug: string;
  card_name: string;
}

export type OutboxStatus = "draft" | "scheduled" | "sending" | "sent" | "canceled" | "failed";

export interface OutboxRow extends CardArtMeta {
  card_send_id: string;
  status: OutboxStatus;
  /** Why a `failed` send failed — `entitlement_lapsed` rows show on Upcoming (D5). */
  failure_reason?: string | null;
  send_at: string | null;
  /** send_at as a wall-clock in the VIEWER's current account zone (re-derived by the
   *  server at read time, spec 14 §103 — legacy parity); null for send-now cards. */
  scheduled_local_at: string | null;
  /** The IANA zone scheduled_local_at is in (null when it has none) — for the zone tag. */
  scheduled_tz?: string | null;
  sent_at: string | null;
  read_slug: string | null;
  updated_at: string;
  created_at: string;
  recipients: SignedRecipient[];
  /** Live (non-canceled/expired) gift cards on the send — the drafts Delete warning. */
  gift_count: number;
  signers: SignerEntry[];
  /** BIGINT — serialized as a string by the API (no int8 parser). */
  card_no: string;
  slug: string;
  card_name: string;
}

export interface SignedRecipient {
  card_send_recipient_id: string;
  recipient_name: string | null;
  recipient_email: string | null;
  recipient_status: string;
  /** Live gift cards attached to this recipient (legacy "gift card" tag). */
  gift_count: number;
}

/** Legacy signers column entry (old card_signer roster: invite name/email + signed state). */
export interface SignerEntry {
  invite_name: string | null;
  invite_email: string | null;
  first_signed_at: string | null;
}

export interface SignedRow extends CardArtMeta {
  card_send_id: string;
  status: OutboxStatus;
  send_at: string | null;
  /** send_at as a wall-clock in the VIEWER's current account zone (re-derived by the
   *  server at read time, spec 14 §103 — legacy parity); null for send-now cards. */
  scheduled_local_at: string | null;
  /** The IANA zone scheduled_local_at is in (null when it has none) — for the zone tag. */
  scheduled_tz?: string | null;
  sent_at: string | null;
  from_email: string | null;
  from_name: string | null;
  read_slug: string | null;
  /** Live sign link while the card is unsent (draft/scheduled) — edit your signature. */
  sign_token: string | null;
  recipients: SignedRecipient[];
  /** The card's signer roster (you included), like Sent's signers column (BDAY-289).
   *  Optional: an older server omits it. */
  signers?: SignerEntry[];
  /** BIGINT — serialized as a string by the API (no int8 parser). */
  card_no: string;
  slug: string;
  card_name: string;
}

export interface FavoriteItem extends CardArtMeta {
  card_id: string;
  /** BIGINT — serialized as a string by the API (no int8 parser). */
  card_no: string;
  slug: string;
  card_name: string;
  is_premium: boolean;
  /** Optional: an older server sends neither; the tile falls back to the card fetch. */
  is_new?: boolean;
  card_type?: "static" | "video" | "custom" | "gif" | "dynamic";
  favorited_at: string;
  /** Last time the member sent or scheduled this card (null = never); the list is ordered by it (BDAY-513). */
  last_used_at?: string | null;
}

/** The live attachment of a Library gift to one recipient of a card send (null = unattached). */
export interface GiftAttachment {
  cardSendId: string;
  sendStatus: OutboxStatus;
  readSlug: string | null;
  sentAt: string | null;
  recipientId: string | null;
  recipientName: string | null;
  /** Recipient email + their per-recipient delivery status (opened/delivered/…), as legacy showed. */
  recipientEmail: string | null;
  recipientStatus: string | null;
}

/**
 * One gift in the member's Library (GET /me/gift-cards — PRD 10 §6). A redemption URL is
 * NEVER in this payload; "Share Redeem Link" fetches one on demand (revealRedeemLink, §12),
 * unlike legacy which leaked a `redeemUrl` for every gift into the list response.
 */
export interface GiftItem {
  giftCardId: string;
  status: GiftStatus;
  brandName: string | null;
  productTitle: string | null;
  /** Branded retailer art the server already resolved (null for sim / logo-less gifts). */
  imageUrl: string | null;
  amountMinor: number | null;
  currencyCode: string | null;
  orderRef: string | null;
  qrImageUrl: string | null;
  issuedAt: string | null;
  deliveredAt: string | null;
  redeemedAt: string | null;
  createdAt: string;
  attachment: GiftAttachment | null;
}

export const MY_CARDS_QUERY_KEY = "/api/user/my-cards";

/**
 * Query keys, all under {@link MY_CARDS_QUERY_KEY} so one invalidation clears
 * every tab. The web page pages with an explicit `page` slot; the mobile app
 * uses `useInfiniteQuery` (React Query owns the pages) so it keys page-less via
 * the `*List` helpers.
 */
export const myCardsKeys = {
  inbox: (page: number, onlyHidden: boolean) =>
    [MY_CARDS_QUERY_KEY, "inbox", page, onlyHidden] as const,
  outbox: (view: string, page: number, contactId?: string | null) =>
    [MY_CARDS_QUERY_KEY, "outbox", view, page, contactId ?? null] as const,
  library: (view: string) => [MY_CARDS_QUERY_KEY, "library", view] as const,
  /** Favorites as catalog tiles (reminder browse "My Favorites") — nested under the
   *  favorites library key so a heart toggle's invalidation reaches it too. */
  favoriteTiles: () => [MY_CARDS_QUERY_KEY, "library", "favorites", "tiles"] as const,
  inboxList: (onlyHidden: boolean) => [MY_CARDS_QUERY_KEY, "inbox", "list", onlyHidden] as const,
  outboxList: (view: string) => [MY_CARDS_QUERY_KEY, "outbox", "list", view] as const,
};

/**
 * Mobile pretty date (old getShortMonthPrettyDate): "Jul 28th, 2026 at 08:00 am"
 * (hour zero-padded, lowercase am/pm). Dependency-free so mobile needn't add
 * date-fns. Accepts a full ISO timestamp (rendered in the viewer's zone) or a
 * zoneless wall-clock string ("2026-07-25T08:00", parsed as local, i.e. verbatim).
 */
export function formatCardDateTimeShort(iso: string | null | undefined): string {
  return formatCardDateTimeWith(iso, (m) => m.slice(0, 3));
}

/**
 * The FULL-month form: "July 17th, 2026 at 08:00 am" — legacy's `getPrettyDate`, and what
 * the gift library row uses on every width (unlike the other My Cards tabs, which use the
 * short month on mobile). The web twin is `formatCardDateTime` in the site's my-cards-data,
 * via date-fns `MMMM do, yyyy 'at' hh:mm aaa`; this is the dependency-free mirror.
 */
export function formatCardDateTimeLong(iso: string | null | undefined): string {
  return formatCardDateTimeWith(iso, (m) => m);
}

function formatCardDateTimeWith(
  iso: string | null | undefined,
  monthForm: (full: string) => string,
): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const month = monthForm(MONTH_NAMES[d.getMonth()]!);
  const day = ordinal(d.getDate());
  let hour = d.getHours();
  const ap = hour < 12 ? "am" : "pm";
  hour = hour % 12 || 12;
  const hh = String(hour).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${month} ${day}, ${d.getFullYear()} at ${hh}:${mm} ${ap}`;
}

const MONTH_YEAR = new Intl.DateTimeFormat("en-US", { month: "short", year: "numeric" });

/**
 * The line under a favorite (BDAY-513): "Last sent Oct 2025", or "Scheduled Nov 2026" for a
 * card the member has scheduled but not sent yet; null when never sent.
 */
export function favoriteLastSentLabel(
  lastUsedAt: string | null | undefined,
  now: Date = new Date(),
): string | null {
  if (!lastUsedAt) return null;
  const d = new Date(lastUsedAt);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getTime() > now.getTime() ? "Scheduled" : "Last sent"} ${MONTH_YEAR.format(d)}`;
}
